import { configure } from 'mobx';
import React from 'react';
import { observer, Provider as MobXProvider } from 'mobx-react';
import { syncHistoryWithStore } from '@ibm/mobx-react-router';
import { Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import mixpanel from 'mixpanel-browser';

import { injectStyle } from 'react-toastify/dist/inject-style';

import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// UK locale so date pickers start the week on Monday (and follow UK conventions).
import 'dayjs/locale/en-gb';

import { StyledToastContainer } from 'core/styles';

import { browserHistory } from 'utils/history';

import SystemError from 'containers/Error/Pages/SystemError/SystemError';

import { env } from 'config';

import rootStore from '../../stores';
import Routing from './components/Routing';
import Session from '../Session';
import StyleProvider from './components/StyleProvider';
import { SessionStoreWithUser } from '../../stores/Session/types';
import DynamicManifestWrapper from '../../components/DynamicManifestWrapper/DynamicManifestWrapper';
import KnockGuidesProvider from '../../components/KnockGuidesProvider';

injectStyle();

configure({ enforceActions: 'always' });

const history = syncHistoryWithStore(browserHistory, rootStore.stores.routing);
history.listen(({ location }) => {
  if (env.JEST_WORKER_ID === undefined) {
    const session = rootStore.stores.session as SessionStoreWithUser;
    const trackingData: { page: string; user_email?: string; user_id?: string } = {
      page: location.pathname,
    };

    if (session.user) {
      trackingData.user_email = session.user.email;
      trackingData.user_id = session.user.uuid;
    }

    mixpanel.track_pageview(trackingData);
  }
});

dayjs.extend(utc);
dayjs.extend(timezone);

// Read via `config` (not `import.meta.env`) so it resolves under Jest, which
// mocks the `config` module — `import.meta` would be a parse error there.
const prototypeBasename =
  env.REACT_APP_IS_PROTOTYPE === 'true'
    ? (window as Window & { __PROTOTYPE_BASENAME__?: string }).__PROTOTYPE_BASENAME__
    : undefined;

// utils/history.ts strips the basename so MobX routing-store consumers see
// inner-route paths, but `<Router basename>` re-strips it itself — feeding it
// the stripped path renders blank. Reconstruct the raw path for Router.
const ObserverRouter: React.FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const innerLocation = rootStore.stores.routing.location;
  const routerLocation = prototypeBasename
    ? {
        ...innerLocation,
        pathname: innerLocation.pathname === '/' ? prototypeBasename : `${prototypeBasename}${innerLocation.pathname}`,
      }
    : innerLocation;
  return (
    <Router basename={prototypeBasename} location={routerLocation} navigator={history}>
      {children}
    </Router>
  );
});

const App: React.FunctionComponent = (): React.ReactElement => (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
    <StyleProvider>
      <MobXProvider {...rootStore.stores}>
        <Sentry.ErrorBoundary fallback={<SystemError />}>
          <CssBaseline />
          <Session>
            <KnockGuidesProvider>
              <ObserverRouter>
                <DynamicManifestWrapper />
                <Routing />
                <StyledToastContainer limit={5} />
              </ObserverRouter>
            </KnockGuidesProvider>
          </Session>
        </Sentry.ErrorBoundary>
      </MobXProvider>
    </StyleProvider>
  </LocalizationProvider>
);

export default App;
